import { useState, useEffect } from 'react';
import { api } from '../api/client';

interface BankRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  preloadedFile?: File | null;
}

export default function BankRequestModal({ isOpen, onClose, preloadedFile }: BankRequestModalProps) {
  const [bankName, setBankName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [sampleStatement, setSampleStatement] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set preloaded file when modal opens
  useEffect(() => {
    if (isOpen && preloadedFile) {
      setSampleStatement(preloadedFile);
    }
  }, [isOpen, preloadedFile]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSampleStatement(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await api.submitBankRequest({
        bankName,
        contactEmail: contactEmail || undefined,
        sampleStatement: sampleStatement || undefined,
      });

      setSuccess(true);
      setTimeout(() => {
        onClose();
        // Reset form
        setBankName('');
        setContactEmail('');
        setSampleStatement(null);
        setSuccess(false);
      }, 2000);
    } catch (err: any) {
      console.error('Bank request submission failed:', err);
      setError(err.response?.data?.message || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      // Reset form
      setBankName('');
      setContactEmail('');
      setSampleStatement(null);
      setError(null);
      setSuccess(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-70">
      <div className="relative w-full max-w-lg bg-white border-4 border-black">
        <div className="absolute -top-1 -right-1 w-full h-full bg-blue-500 -z-10"></div>

        {/* Header */}
        <div className="p-6 border-b-4 border-black bg-white">
          <h2 className="text-3xl font-black text-black uppercase tracking-tighter">
            Request New Bank
          </h2>
          <p className="mt-2 text-sm font-mono text-gray-600">
            We'll review your request and add support for your bank
          </p>
        </div>

        {/* Content */}
        <div className="p-6 bg-white">
          {success ? (
            <div className="p-6 bg-green-500 border-4 border-black flex items-center justify-center space-x-3">
              <div className="text-4xl">✓</div>
              <div>
                <h3 className="font-black text-white uppercase tracking-wider text-lg">Success!</h3>
                <p className="text-white text-sm mt-1 font-mono">Request submitted successfully</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Bank Name */}
              <div>
                <label className="block text-xs font-mono text-gray-700 uppercase tracking-wider mb-2 font-bold">
                  Bank Name *
                </label>
                <input
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-black font-bold focus:outline-none focus:ring-4 focus:ring-blue-300"
                  placeholder="e.g., Banque Populaire"
                  required
                />
              </div>

              {/* Contact Email */}
              <div>
                <label className="block text-xs font-mono text-gray-700 uppercase tracking-wider mb-2 font-bold">
                  Contact Email (Optional)
                </label>
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-black font-bold focus:outline-none focus:ring-4 focus:ring-blue-300"
                  placeholder="your@email.com"
                />
                <p className="mt-1 text-xs font-mono text-gray-500">
                  We'll notify you when support is added
                </p>
              </div>

              {/* Sample Statement Upload */}
              <div>
                <label className="block text-xs font-mono text-gray-700 uppercase tracking-wider mb-2 font-bold">
                  Sample Statement {preloadedFile ? '(Already Attached)' : '(Optional)'}
                </label>
                <div className="relative">
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.csv,.xls,.xlsx,.txt"
                    className="hidden"
                    id="sample-statement-upload"
                  />
                  <label
                    htmlFor="sample-statement-upload"
                    className={`relative block w-full px-4 py-3 border-2 border-black font-bold text-center cursor-pointer transition-colors ${
                      preloadedFile
                        ? 'bg-emerald-100 hover:bg-emerald-200'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {sampleStatement ? (
                      <span className="text-sm flex items-center justify-center space-x-2">
                        {preloadedFile && <span>✓</span>}
                        <span>📄 {sampleStatement.name}</span>
                      </span>
                    ) : (
                      <span className="text-sm text-gray-600">
                        Click to upload statement
                      </span>
                    )}
                  </label>
                </div>
                <p className="mt-1 text-xs font-mono text-gray-500">
                  {preloadedFile
                    ? 'Your uploaded statement is already attached. You can replace it if needed.'
                    : 'Helps us add support faster (PDF, CSV, Excel, TXT)'}
                </p>
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-4 bg-red-500 border-4 border-black flex items-start space-x-3">
                  <div className="text-2xl">❌</div>
                  <div>
                    <h3 className="font-black text-white uppercase tracking-wider text-sm">Error</h3>
                    <p className="text-white text-xs mt-1 font-mono">{error}</p>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex space-x-4 pt-2">
                <button
                  type="submit"
                  disabled={loading || !bankName.trim()}
                  className="relative flex-1 px-6 py-3 bg-blue-500 text-white font-black border-4 border-blue-500 transition-transform hover:translate-x-1 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-x-0 disabled:hover:translate-y-0"
                >
                  <div className="absolute -top-1 -right-1 w-full h-full bg-blue-700 -z-10"></div>
                  {loading ? 'SUBMITTING...' : 'SUBMIT REQUEST'}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading}
                  className="relative flex-1 px-6 py-3 bg-white text-black font-black border-4 border-black transition-transform hover:translate-x-1 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="absolute -top-1 -right-1 w-full h-full bg-gray-400 -z-10"></div>
                  CANCEL
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
